import { type RefObject, useCallback, useState } from 'react';
import {
  faArrowUpFromBracket,
  faImage,
  faPencil,
  faTrash,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { Measurable } from '@radix-ui/rect';
import { toast } from 'sonner';

import { ImageCropModal } from '@/components/asset-manager/components/image-crop-modal';
import type { CropData } from '@/components/asset-manager/components/image-crop-modal/types';
import type { Asset } from '@/components/asset-manager/types';
import type {
  ImageBlockAspect,
  ImageBlockFit,
  ImageBlockShape,
} from '@/components/blocks/primitives/image-block';
import {
  IMAGE_ASPECT_OPTIONS,
  selectedImageAspect,
} from '@/components/blocks/primitives/image-presentation';
import {
  ImageWithLoading,
  NoImagePlaceholder,
} from '@/components/blocks/section-image/image-with-loading';
import { ImageSelectorModal } from '@/components/editor/image-selector-modal';
import { OptionButtonGroup, SettingRow } from '@/components/page-editor/settings-panel/fields';
import { FloatWrapControl } from '@/components/page-editor/settings-panel/float-wrap-control';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { RadioCardGroup } from '@/components/ui/form/radio-card-group';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MutedText, SmallText } from '@/components/ui/typography';
import { IMAGE_SIZE_OPTIONS, type RequestedImageSize } from '@/lib/blocks/image-size';
import type { GroupChildFloat } from '@/lib/blocks/layout/group-layout';
import http from '@/lib/http';
import { DEFAULT_FOCUS_POINT } from '@/lib/prop-utils';
import { cmsAssetCropperPath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import type { ImageSuggestionTarget, ImageValue, SelectedAssetData } from '@/types/image';
import { getImageAlt, getImageUrl } from '@/types/image';
import { FocusPointOverlay } from './focus-point-overlay';
import { SettingsPopoverShell } from './settings-popover-shell';

type ShapeUI = 'sharp' | 'rounded' | 'circle';

function resolveShapeForUI(shape: ImageBlockShape | undefined): ShapeUI {
  if (shape === 'circle') return 'circle';
  if (shape === 'sharp') return 'sharp';
  return 'rounded';
}

const FIT_OPTIONS = [
  { value: 'cover' as const, label: 'Fill' },
  { value: 'contain' as const, label: 'Fit' },
];

function ShapePreviewIcon({ shape }: { shape: ShapeUI }) {
  const base = 'size-8 border-2 border-foreground/60';
  switch (shape) {
    case 'sharp':
      return <div className={cn(base, 'rounded-none')} />;
    case 'rounded':
      return <div className={cn(base, 'rounded-md')} />;
    case 'circle':
      return <div className={cn(base, 'rounded-full')} />;
  }
}

const SHAPE_OPTIONS = [
  { value: 'sharp' as const, title: 'Sharp', preview: <ShapePreviewIcon shape="sharp" /> },
  { value: 'rounded' as const, title: 'Rounded', preview: <ShapePreviewIcon shape="rounded" /> },
  { value: 'circle' as const, title: 'Circle', preview: <ShapePreviewIcon shape="circle" /> },
];

export interface ImageSettingsPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  image: ImageValue | null | undefined;
  fit?: ImageBlockFit;
  shape?: ImageBlockShape;
  showSettings?: boolean;
  selectedAssetId?: string | null;
  selectedFolderId?: string | null;
  initialImageUrl?: string | null;
  anchorRef?: RefObject<Measurable | null>;
  onImageChange: (image: ImageValue | null) => void;
  onImageCropped?: (result: ImageCropResult) => void;
  onAltTextChange: (altText: string) => void;
  onFitChange?: (fit: ImageBlockFit) => void;
  onShapeChange?: (shape: ImageBlockShape) => void;
  aspect?: ImageBlockAspect;
  onAspectChange?: (aspect: ImageBlockAspect) => void;
  imageSize?: RequestedImageSize;
  onImageSizeChange?: (imageSize: RequestedImageSize) => void;
  focusX?: number;
  focusY?: number;
  onFocusChange?: (x: number, y: number) => void;
  duplicateOnCrop?: boolean;
  onSave: () => void;
  onCancel: () => void;
  sectionId?: string;
  imageSuggestionTarget?: ImageSuggestionTarget;
  enableCreateImage?: boolean;
  float?: GroupChildFloat | null;
  onFloatChange?: (float: GroupChildFloat | null) => void;
}

export interface ImageCropResult {
  image: ImageValue;
  width?: number;
  height?: number;
}

export function ImageSettingsPopover({
  open,
  onOpenChange,
  image,
  fit = 'cover',
  shape,
  showSettings = false,
  selectedAssetId,
  selectedFolderId,
  initialImageUrl,
  anchorRef,
  onImageChange,
  onImageCropped,
  onAltTextChange,
  onFitChange,
  onShapeChange,
  aspect,
  onAspectChange,
  imageSize = 'auto',
  onImageSizeChange,
  focusX = DEFAULT_FOCUS_POINT,
  focusY = DEFAULT_FOCUS_POINT,
  onFocusChange,
  duplicateOnCrop,
  onSave,
  onCancel,
  sectionId,
  imageSuggestionTarget,
  enableCreateImage,
  float = null,
  onFloatChange,
}: ImageSettingsPopoverProps) {
  const [activeTab, setActiveTab] = useState<'image' | 'settings'>('image');
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);

  const hasImage = image != null && getImageUrl(image) != null;
  const originalUrl = image?.url;
  const assetId = image?.assetId;
  const altText = getImageAlt(image);

  const handleImageSelected = useCallback(
    (data: SelectedAssetData) => {
      onImageChange({
        assetId: data.assetId,
        url: data.url,
        alt: data.altText,
        folderId: data.folderId,
        variants: data.variants,
        contentType: data.contentType,
      });
      setSelectorOpen(false);
    },
    [onImageChange],
  );

  const handleCropSave = useCallback(
    async (cropData: CropData) => {
      if (!assetId) return;

      try {
        const response = await http.post(cmsAssetCropperPath(assetId), {
          crop: cropData.croppedAreaPixels,
          rotation: cropData.rotation,
          flipHorizontal: cropData.flipHorizontal,
          flipVertical: cropData.flipVertical,
          ...(duplicateOnCrop && { duplicate: true }),
        });

        const updatedAsset = response.data as Asset;
        const croppedImage: ImageValue = {
          assetId: updatedAsset.id,
          url: updatedAsset.url,
          alt: altText,
          folderId: updatedAsset.folderId,
          variants: updatedAsset.variants,
          contentType: updatedAsset.contentType,
        };
        if (onImageCropped) {
          onImageCropped({
            image: croppedImage,
            width: updatedAsset.width,
            height: updatedAsset.height,
          });
        } else {
          onImageChange(croppedImage);
        }
        setCropOpen(false);
      } catch {
        toast.error('Failed to crop image. Please try again.');
      }
    },
    [assetId, altText, duplicateOnCrop, onImageChange, onImageCropped],
  );

  return (
    <>
      <SettingsPopoverShell
        open={open}
        onOpenChange={onOpenChange}
        anchorRef={anchorRef}
        title="Image Settings"
        hasSubModalOpen={selectorOpen || cropOpen}
        trackMovingAnchor={Boolean(onFloatChange)}
        onSave={onSave}
        onCancel={onCancel}
      >
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'image' | 'settings')}>
          <TabsList variant="underline" fullWidth={false} className="max-w-none w-full">
            <TabsTrigger value="image">Image</TabsTrigger>
            {showSettings && <TabsTrigger value="settings">Settings</TabsTrigger>}
          </TabsList>

          <TabsContent value="image" className="mt-2">
            {hasImage ? (
              <div className="space-y-4">
                <div className="relative overflow-hidden rounded-md border">
                  <ImageWithLoading
                    image={image}
                    alt={altText || 'Preview'}
                    className="max-h-[200px] w-full object-contain"
                    fallback={<NoImagePlaceholder />}
                  />
                  {onFocusChange && (
                    <FocusPointOverlay
                      focusX={focusX}
                      focusY={focusY}
                      onFocusChange={onFocusChange}
                    />
                  )}
                  <Button
                    size="icon-sm"
                    variant="solid"
                    colorScheme="destructive"
                    className="absolute right-2 top-2 z-10"
                    onClick={() => onImageChange(null)}
                  >
                    <Icon icon={faTrash} />
                    <span className="sr-only">Remove image</span>
                  </Button>
                </div>

                {assetId ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      colorScheme="secondary"
                      onClick={() => setCropOpen(true)}
                    >
                      <Icon icon={faPencil} className="size-4" />
                      Edit Image
                    </Button>
                    <Button
                      variant="outline"
                      colorScheme="secondary"
                      onClick={() => setSelectorOpen(true)}
                    >
                      <Icon icon={faArrowUpFromBracket} className="size-4" />
                      Replace
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    colorScheme="secondary"
                    className="w-full"
                    onClick={() => setSelectorOpen(true)}
                  >
                    <Icon icon={faArrowUpFromBracket} className="size-4" />
                    Replace
                  </Button>
                )}

                <div className="space-y-1.5">
                  <SmallText className="font-semibold text-foreground">Alt Text</SmallText>
                  <MutedText className="text-xs">
                    Describe the image for search engines and screen readers
                  </MutedText>
                  <Input
                    value={altText}
                    onChange={(e) => onAltTextChange(e.target.value)}
                    placeholder="Describe this image"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 py-12">
                <Button
                  variant="outline"
                  colorScheme="secondary"
                  onClick={() => setSelectorOpen(true)}
                >
                  <Icon icon={faImage} className="size-4" />
                  Select Image
                </Button>
              </div>
            )}
          </TabsContent>

          {showSettings && (
            <TabsContent value="settings" className="mt-2">
              <div className="space-y-6">
                <SettingRow
                  title="Image Fit"
                  description="Choose whether the image fills the entire area or fits within it"
                >
                  <ButtonGroup>
                    {FIT_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        variant={fit === option.value ? 'solid' : 'outline'}
                        colorScheme="secondary"
                        onClick={() => onFitChange?.(option.value)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </SettingRow>

                {hasImage && onAspectChange && shape !== 'circle' && (
                  <SettingRow title="Image Aspect" description="Shape of the image frame">
                    <OptionButtonGroup<ImageBlockAspect>
                      options={IMAGE_ASPECT_OPTIONS}
                      value={selectedImageAspect(aspect)}
                      onChange={onAspectChange}
                      buttonClassName="px-2 text-xs font-semibold"
                    />
                  </SettingRow>
                )}

                {hasImage && onImageSizeChange && (
                  <SettingRow
                    title="Image Size"
                    description="Controls image width while preserving its aspect ratio"
                  >
                    <div className="space-y-1.5">
                      <OptionButtonGroup<RequestedImageSize>
                        options={IMAGE_SIZE_OPTIONS}
                        value={imageSize}
                        onChange={onImageSizeChange}
                        buttonClassName="min-w-9 px-2 text-xs font-semibold"
                      />
                      {imageSize === 'auto' && (
                        <SmallText className="text-right text-xs text-muted-foreground">
                          Using original dimensions
                        </SmallText>
                      )}
                    </div>
                  </SettingRow>
                )}

                <div className="space-y-2">
                  <SmallText className="font-semibold text-foreground">Shape</SmallText>
                  <RadioCardGroup
                    value={resolveShapeForUI(shape)}
                    onValueChange={(v) => onShapeChange?.(v as ImageBlockShape)}
                    options={SHAPE_OPTIONS}
                    gridColumns="grid-cols-3"
                    gridGap="gap-2"
                    hideRadio
                    variant="preview"
                  />
                </div>

                {onFloatChange && (
                  <FloatWrapControl
                    value={float}
                    onChange={onFloatChange}
                    description="Float this image so surrounding content in the same group wraps around it"
                  />
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </SettingsPopoverShell>

      <ImageSelectorModal
        isOpen={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSelectImage={handleImageSelected}
        fileTypes="images"
        selectLabel="Apply"
        selectedAssetId={selectedAssetId ?? undefined}
        selectedFolderId={selectedFolderId ?? undefined}
        initialImageUrl={selectedAssetId ? undefined : (initialImageUrl ?? undefined)}
        sectionId={sectionId}
        imageSuggestionTarget={imageSuggestionTarget}
        enableCreateImage={enableCreateImage}
      />

      {cropOpen && originalUrl && assetId && (
        <ImageCropModal
          imageUrl={originalUrl}
          onSave={handleCropSave}
          onCancel={() => setCropOpen(false)}
          isOpen={cropOpen}
        />
      )}
    </>
  );
}
